<template>
    <kiosk-team-term-details :team-id="props.teamId" :term="props.term">
        <div class="col-span-12 lg:col-span-10 flex flex-col gap-6 w-full max-w-(--breakpoint-sm)">
            <vx-alert v-if="!canEdit" color="info" shadow :closeable="false" data-test="published-warning">
                This term is <strong>published</strong> and can no longer be edited.
            </vx-alert>

            <term-form-title v-model="data" :can-edit="canEdit" />
            <term-form-description v-model="data" :can-edit="canEdit" />
            <term-form-content v-model="data" :can-edit="canEdit" />
            <term-form-effective-at v-model="data" :can-edit="canEdit" />
            <term-form-consent v-model="data" :can-edit="canEdit" />

            <div v-if="canPublish" class="sticky z-10 bottom-30">
                <vx-alert shadow :closeable="false" color="info">
                    This term is currently a draft. Once published, it can no longer be edited and will be effective
                    based on the date set above.
                    <template v-slot:right>
                        <vx-button @click="confirmPublish()" data-test="publish-button" size="sm" color="info">
                            Publish
                        </vx-button>
                    </template>
                </vx-alert>
            </div>

            <vx-sticky-footer>
                <vx-button :href="backHref" class="w-20" size="lg" color="muted">Cancel</vx-button>

                <vx-popover class="grow" hover arrow placement="top">
                    <vx-button
                        @click="submit()"
                        data-test="submit-button"
                        block
                        size="lg"
                        color="primary"
                        :loading="processing"
                        :disabled="!canEdit"
                    >
                        Update Term
                    </vx-button>
                    <template v-if="!canEdit" v-slot:content>
                        <p class="max-w-xs text-sm text-center">
                            Published terms cannot be edited. Please create a new term if you would like to make
                            changes.
                        </p>
                    </template>
                </vx-popover>
            </vx-sticky-footer>
        </div>
    </kiosk-team-term-details>
</template>

<script lang="ts" setup>
import { VxAlert, VxPopover, VxButton, VxStickyFooter, VxSwal } from '@voxie/frontend-components';
import KioskTeamTermDetails from './KioskTeamTermDetails.vue';
import TermFormTitle from './TermFormTitle.vue';
import TermFormDescription from './TermFormDescription.vue';
import TermFormContent from './TermFormContent.vue';
import TermFormEffectiveAt from './TermFormEffectiveAt.vue';
import TermFormConsent from './TermFormConsent.vue';
import { useTermFormSubmission } from './useTermFormSubmission';
import { throwSwalRequestError } from '~/utils/request';
import type { Term } from '~/services/adminTermsClient.types';

const props = defineProps<{
    teamId: number;
    term: Term;
}>();

const { data, submit, processing, canEdit, canPublish, publish, backHref } = useTermFormSubmission(
    props.teamId,
    props.term
);

const confirmPublish = () => {
    VxSwal.fire({
        title: 'Publish Term',
        text: 'Once published, this term can no longer be edited. Continue?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'primary',
        preConfirm: () => publish().catch(throwSwalRequestError),
    });
};
</script>
